import { Box, Theme, TypographyProps, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties, FC, ReactNode } from 'react';
import { WbCardHeaderStub } from './WbCardHeaderStub';

export type WbCardStubVariant = 'default' | 'secondary';

export interface WbCardStubProps {
  children?: ReactNode;
  title?: ReactNode;
  titleId?: string;
  icon?: JSX.Element;
  actions?: JSX.Element;
  leading?: JSX.Element;
  typographyProps?: TypographyProps;
  headerStyle?: CSSProperties;
  headerContentStyle?: CSSProperties;
  footer?: JSX.Element;
  toolsComponents?: JSX.Element;
  statusComponent?: JSX.Element;
  cardClassName?: string;
  cardStyle?: CSSProperties;
  footerStyle?: CSSProperties;
  toolsStyle?: CSSProperties;
  toolsBarStyle?: CSSProperties;
  fillContainer?: boolean;
  titleContainerStyle?: CSSProperties;
  variant?: WbCardStubVariant;
}

type UseStylesProps = {
  hasTools: boolean;
  fillContainer: boolean;
  variant: WbCardStubVariant;
};

const useStyles = makeStyles<Theme, UseStylesProps>(theme => ({
  card: ({ fillContainer, variant }) => {
    return {
      width: '100%',
      display: 'flex',
      height: fillContainer ? '100%' : undefined,
      flexDirection: 'column',
      background: theme.palette.background.default,
      boxShadow: theme.shadows[1],
      containerType: 'inline-size',
      ...(variant === 'default' && {
        borderRadius: theme.spacing(0.5),
      }),
    };
  },
  bar: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderLeft: 'none',
    borderRight: 'none',
    borderTop: `none`,
  },
  toolsBar: {
    padding: '8px 12px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  footer: {
    borderTop: `1px solid ${theme.palette.grey[200]}`,
  },
  statusBar: ({ hasTools }) => ({
    height: '48px',
    width: '30%',
    borderLeft: hasTools ? 'none' : `1px solid ${theme.palette.grey[200]}`,
    display: 'flex',
    alignItems: 'center',
  }),
}));

export const WbCardStub: FC<WbCardStubProps> = ({
  title,
  titleId,
  icon,
  actions,
  headerStyle,
  headerContentStyle,
  typographyProps,
  leading,
  titleContainerStyle,
  toolsComponents,
  statusComponent,
  cardClassName,
  children,
  cardStyle,
  footerStyle,
  toolsStyle,
  toolsBarStyle,
  footer,
  fillContainer,
  variant = 'default',
}) => {
  const classes = useStyles({
    hasTools: Boolean(toolsComponents),
    fillContainer: Boolean(fillContainer),
    variant: variant,
  });

  const showHeader =
    title !== undefined ||
    icon !== undefined ||
    actions !== undefined ||
    titleId !== undefined ||
    leading !== undefined ||
    headerStyle !== undefined ||
    headerContentStyle !== undefined ||
    typographyProps !== undefined ||
    titleContainerStyle !== undefined;

  return (
    <Box className={clsx(classes.card, cardClassName)} style={cardStyle}>
      {showHeader && (
        <WbCardHeaderStub
          titleId={titleId}
          title={title}
          icon={icon}
          actions={actions}
          style={headerStyle}
          contentStyle={headerContentStyle}
          typographyProps={typographyProps}
          leading={leading}
          titleContainerStyle={titleContainerStyle}
          variant={variant}
        />
      )}
      {!!toolsComponents || !!statusComponent ? (
        <Box className={classes.bar} style={toolsStyle}>
          {!!toolsComponents ? (
            <Box className={classes.toolsBar} style={toolsBarStyle}>
              {toolsComponents}
            </Box>
          ) : (
            <Box width="100%" />
          )}
          {!!statusComponent && (
            <Box className={classes.statusBar}>{statusComponent}</Box>
          )}
        </Box>
      ) : (
        <></>
      )}
      {children}
      {footer && (
        <Box className={classes.footer} style={footerStyle}>
          {footer}
        </Box>
      )}
    </Box>
  );
};
